import { SortDirection } from "./sort-direction";

/** A decoded `sort` query value: which field, which way. */
export type ParsedSort = {
  field: string;
  direction: SortDirection;
};

/**
 * Decode the canonical query-params sort STRING (`field` asc / `-field` desc —
 * see .claude/rules/query-params.md). Absent, empty, and the bare `"-"` all
 * read as "no sort". Inverse of `formatSortParam`; the `-` prefix is decoded
 * here and nowhere else.
 */
export function parseSortParam(
  sort: string | undefined,
): ParsedSort | undefined {
  if (!sort || sort === "-") return undefined;
  return sort.startsWith("-")
    ? { field: sort.slice(1), direction: SortDirection.DESCENDING }
    : { field: sort, direction: SortDirection.ASCENDING };
}

/** Encode a field + direction back into the canonical sort string. */
export function formatSortParam(
  field: string,
  direction: SortDirection,
): string {
  return direction === SortDirection.DESCENDING ? `-${field}` : field;
}

/**
 * Next sort value when a column header is clicked: inactive → ascending →
 * descending → `defaultSort` (pass `undefined` to clear the param instead).
 * A screen whose UI renders a default sort while the param is unset passes the
 * EFFECTIVE current value (`sort || DEFAULT_SORT`) so the default column
 * cycles from its rendered direction rather than re-activating ascending.
 */
export function cycleSortValue(
  current: string | undefined,
  field: string,
  defaultSort?: string,
): string | undefined {
  const active = parseSortParam(current);
  if (active?.field !== field)
    return formatSortParam(field, SortDirection.ASCENDING);
  if (active.direction === SortDirection.ASCENDING)
    return formatSortParam(field, SortDirection.DESCENDING);
  return defaultSort;
}
